import { useEffect, useState, useContext } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  ArrowLeft, Calendar, Clock, MapPin, Users, FileText, 
  Download, Plus, Edit, Trash2, CheckCircle, AlertCircle,
  Send, ClipboardList, Building2, ChevronUp, ChevronDown, Save, X
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ScheduledMeetingDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [meeting, setMeeting] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [orgReports, setOrgReports] = useState([]);
  const [agendaItems, setAgendaItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [minutes, setMinutes] = useState(null);
  const [showAddAgendaDialog, setShowAddAgendaDialog] = useState(false);
  const [showEditMeetingDialog, setShowEditMeetingDialog] = useState(false);
  const [editingAgendaItem, setEditingAgendaItem] = useState(null);
  const [newAgendaItem, setNewAgendaItem] = useState({
    title: '',
    description: '',
    priority: 'normal',
    estimated_duration: 10
  });
  const [editMeeting, setEditMeeting] = useState({});

  const isAdmin = user?.role === 'super_admin' || user?.role === 'admin';

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      const [meetingRes, orgsRes, reportsRes, agendaRes, usersRes, minutesRes] = await Promise.all([
        axios.get(`${API}/scheduled-meetings/${id}`),
        axios.get(`${API}/organizations`),
        axios.get(`${API}/org-reports?meeting_id=${id}`),
        axios.get(`${API}/agenda-items?meeting_id=${id}`),
        axios.get(`${API}/users`),
        axios.get(`${API}/meeting-minutes?meeting_id=${id}`)
      ]);
      
      setMeeting(meetingRes.data);
      setEditMeeting(meetingRes.data);
      setOrganizations(orgsRes.data);
      setOrgReports(reportsRes.data);
      setAgendaItems(agendaRes.data);
      setUsers(usersRes.data.filter(u => u.status === 'active'));
      setMinutes(minutesRes.data[0] || null);
    } catch (error) {
      toast.error('Failed to load meeting data');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateMeeting = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/scheduled-meetings/${id}`, editMeeting);
      toast.success('Meeting updated');
      setShowEditMeetingDialog(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to update meeting');
    }
  };

  const handleDeleteMeeting = async () => {
    if (!window.confirm('Are you sure you want to delete this meeting? This will also delete all associated agenda items, reports, and minutes. This action cannot be undone.')) {
      return;
    }
    try {
      await axios.delete(`${API}/scheduled-meetings/${id}`);
      toast.success('Meeting deleted');
      navigate('/meeting-management');
    } catch (error) {
      toast.error('Failed to delete meeting: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleAddAgendaItem = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/agenda-items`, {
        ...newAgendaItem,
        scheduled_meeting_id: id
      });
      toast.success('Agenda item added');
      setShowAddAgendaDialog(false);
      setNewAgendaItem({ title: '', description: '', priority: 'normal', estimated_duration: 10 });
      fetchData();
    } catch (error) {
      toast.error('Failed to add agenda item: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDeleteAgendaItem = async (itemId) => {
    if (!window.confirm('Delete this agenda item?')) return;
    try {
      await axios.delete(`${API}/agenda-items/${itemId}`);
      toast.success('Agenda item removed');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete agenda item');
    }
  };

  const handleEditAgendaItem = (item) => {
    setEditingAgendaItem({
      ...item,
      _original: { ...item }
    });
  };

  const handleSaveAgendaItem = async () => {
    if (!editingAgendaItem) return;
    try {
      await axios.put(`${API}/agenda-items/${editingAgendaItem.id}`, {
        title: editingAgendaItem.title,
        description: editingAgendaItem.description,
        priority: editingAgendaItem.priority,
        estimated_duration: editingAgendaItem.estimated_duration
      });
      toast.success('Agenda item updated');
      setEditingAgendaItem(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to update: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleCancelEdit = () => {
    setEditingAgendaItem(null);
  };

  const handleMoveAgendaItem = async (itemId, direction) => {
    try {
      await axios.put(`${API}/agenda-items/${itemId}/move?direction=${direction}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to move item');
    }
  };

  const downloadMeetingPack = async () => {
    try {
      toast.info('Generating meeting pack...');
      const response = await axios.get(`${API}/scheduled-meetings/${id}/pack`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `HLRA_Meeting_Pack_${meeting.meeting_date}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Meeting pack downloaded');
    } catch (error) {
      toast.error('Failed to generate meeting pack');
    }
  };

  const createMinutes = async () => {
    try {
      await axios.post(`${API}/meeting-minutes`, {
        scheduled_meeting_id: id
      });
      toast.success('Minutes draft created');
      fetchData();
    } catch (error) {
      toast.error('Failed to create minutes');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const getReportStatus = (orgId) => {
    const report = orgReports.find(r => r.organization_id === orgId);
    if (!report) return { status: 'not_started', label: 'Not Started', color: 'text-gray-400' };
    if (report.status === 'submitted') return { status: 'submitted', label: 'Submitted', color: 'text-green-600', report };
    return { status: 'draft', label: 'Draft', color: 'text-orange-500', report };
  };

  if (loading) {
    return <div className="flex justify-center py-12"><div className="spinner"></div></div>;
  }

  if (!meeting) {
    return <div className="text-center py-12">Meeting not found</div>;
  }

  return (
    <div className="space-y-6 fade-in" data-testid="scheduled-meeting-detail">
      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={() => navigate('/meeting-management')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Meeting Management
        </Button>
        <div className="flex gap-2">
          <Button variant="outline" onClick={downloadMeetingPack}>
            <Download className="w-4 h-4 mr-2" />
            Download Pack
          </Button>
          {isAdmin && (
            <Dialog open={showEditMeetingDialog} onOpenChange={setShowEditMeetingDialog}>
              <DialogTrigger asChild>
                <Button variant="outline">
                  <Edit className="w-4 h-4 mr-2" />
                  Edit Meeting
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Edit Meeting Details</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleUpdateMeeting} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Title</Label>
                    <Input
                      value={editMeeting.title || ''}
                      onChange={(e) => setEditMeeting({...editMeeting, title: e.target.value})}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Date</Label>
                      <Input
                        type="date"
                        value={editMeeting.meeting_date || ''}
                        onChange={(e) => setEditMeeting({...editMeeting, meeting_date: e.target.value})}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Time</Label>
                      <Input
                        type="time"
                        value={editMeeting.meeting_time || ''}
                        onChange={(e) => setEditMeeting({...editMeeting, meeting_time: e.target.value})}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Location</Label>
                    <Input
                      value={editMeeting.location || ''}
                      onChange={(e) => setEditMeeting({...editMeeting, location: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Google Meet Link</Label>
                    <Input
                      value={editMeeting.google_meet_link || ''}
                      onChange={(e) => setEditMeeting({...editMeeting, google_meet_link: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Status</Label>
                    <Select value={editMeeting.status || 'scheduled'} onValueChange={(v) => setEditMeeting({...editMeeting, status: v})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="scheduled">Scheduled</SelectItem>
                        <SelectItem value="in_progress">In Progress</SelectItem>
                        <SelectItem value="completed">Completed</SelectItem>
                        <SelectItem value="cancelled">Cancelled</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Deadline Fields */}
                  <div className="border-t pt-4 mt-4">
                    <h4 className="font-medium text-sm text-gray-700 mb-3">Reminder Deadlines</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Agenda Submissions Due</Label>
                        <Input
                          type="date"
                          value={editMeeting.agenda_deadline || ''}
                          onChange={(e) => setEditMeeting({...editMeeting, agenda_deadline: e.target.value})}
                        />
                        <p className="text-xs text-gray-500">Deadline for submitting agenda items</p>
                      </div>
                      <div className="space-y-2">
                        <Label>Org Reports Due</Label>
                        <Input
                          type="date"
                          value={editMeeting.reports_deadline || ''}
                          onChange={(e) => setEditMeeting({...editMeeting, reports_deadline: e.target.value})}
                        />
                        <p className="text-xs text-gray-500">Deadline for org report submissions</p>
                      </div>
                    </div>
                  </div>
                  
                  <DialogFooter className="flex justify-between sm:justify-between">
                    <Button 
                      type="button" 
                      variant="destructive" 
                      onClick={handleDeleteMeeting}
                      className="mr-auto"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete Meeting
                    </Button>
                    <Button type="submit" className="bg-gradient-to-r from-blue-500 to-green-500">
                      Save Changes
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {/* Meeting Header */}
      <Card>
        <CardContent className="pt-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">{meeting.title}</h1>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="flex items-center gap-2 text-gray-600">
              <Calendar className="w-5 h-5" />
              <span>{formatDate(meeting.meeting_date)}</span>
            </div>
            <div className="flex items-center gap-2 text-gray-600">
              <Clock className="w-5 h-5" />
              <span>{meeting.meeting_time}</span>
            </div>
            <div className="flex items-center gap-2 text-gray-600">
              <MapPin className="w-5 h-5" />
              <span>{meeting.location}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                meeting.status === 'completed' ? 'bg-green-100 text-green-700' :
                meeting.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                'bg-blue-100 text-blue-700'
              }`}>
                {meeting.status || 'Scheduled'}
              </span>
            </div>
          </div>
          {meeting.google_meet_link && (
            <div className="mt-4">
              <a 
                href={meeting.google_meet_link} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline"
              >
                Join Google Meet →
              </a>
            </div>
          )}
        </CardContent>
      </Card>

      <Tabs defaultValue="reports" className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="reports">
            <Building2 className="w-4 h-4 mr-2" />
            Org Reports
          </TabsTrigger>
          <TabsTrigger value="agenda">
            <ClipboardList className="w-4 h-4 mr-2" />
            Agenda Items
          </TabsTrigger>
          <TabsTrigger value="attendees">
            <Users className="w-4 h-4 mr-2" />
            Attendees
          </TabsTrigger>
          <TabsTrigger value="minutes">
            <FileText className="w-4 h-4 mr-2" />
            Minutes
          </TabsTrigger>
        </TabsList>

        {/* Organization Reports Tab */}
        <TabsContent value="reports" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Organization Reports</CardTitle>
              <CardDescription>Monthly reports from partner organizations</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {organizations.map((org) => {
                  const reportStatus = getReportStatus(org.id);
                  return (
                    <div 
                      key={org.id} 
                      className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50"
                    >
                      <div className="flex items-center gap-3">
                        {reportStatus.status === 'submitted' ? (
                          <CheckCircle className="w-5 h-5 text-green-500" />
                        ) : (
                          <AlertCircle className="w-5 h-5 text-gray-400" />
                        )}
                        <div>
                          <p className="font-medium">{org.name}</p>
                          <p className={`text-sm ${reportStatus.color}`}>{reportStatus.label}</p>
                        </div>
                      </div>
                      <Link to={`/org-report/${id}/${org.id}`}>
                        <Button variant="outline" size="sm">
                          {reportStatus.status === 'not_started' ? 'Create Report' : 
                           reportStatus.status === 'draft' ? 'Continue Editing' : 'View Report'}
                        </Button>
                      </Link>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Agenda Items Tab */}
        <TabsContent value="agenda" className="space-y-4">
          <div className="flex justify-end">
            <Dialog open={showAddAgendaDialog} onOpenChange={setShowAddAgendaDialog}>
              <DialogTrigger asChild>
                <Button className="bg-gradient-to-r from-blue-500 to-green-500">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Agenda Item
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Submit Agenda Item</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleAddAgendaItem} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Title *</Label>
                    <Input
                      value={newAgendaItem.title}
                      onChange={(e) => setNewAgendaItem({...newAgendaItem, title: e.target.value})}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Description *</Label>
                    <Textarea
                      value={newAgendaItem.description}
                      onChange={(e) => setNewAgendaItem({...newAgendaItem, description: e.target.value})}
                      required
                      rows={4}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Priority</Label>
                      <Select value={newAgendaItem.priority} onValueChange={(v) => setNewAgendaItem({...newAgendaItem, priority: v})}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="low">Low</SelectItem>
                          <SelectItem value="normal">Normal</SelectItem>
                          <SelectItem value="high">High</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Duration (mins)</Label>
                      <Input
                        type="number"
                        value={newAgendaItem.estimated_duration}
                        onChange={(e) => setNewAgendaItem({...newAgendaItem, estimated_duration: parseInt(e.target.value)})}
                        min={5}
                        max={60}
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button type="submit" className="bg-gradient-to-r from-blue-500 to-green-500">
                      Submit
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Discussion Items ({agendaItems.length})</CardTitle>
              <CardDescription>Items submitted for discussion at this meeting. Use arrows to reorder.</CardDescription>
            </CardHeader>
            <CardContent>
              {agendaItems.length === 0 ? (
                <p className="text-center text-gray-500 py-8">No agenda items submitted yet</p>
              ) : (
                <div className="space-y-3">
                  {agendaItems.map((item, index) => {
                    const isEditing = editingAgendaItem?.id === item.id;
                    const canEdit = item.submitted_by === user?.id || isAdmin;
                    
                    return (
                      <div key={item.id} className={`p-4 border rounded-lg transition-all ${isEditing ? 'ring-2 ring-blue-500 bg-blue-50' : ''}`} data-testid={`agenda-item-${index + 1}`}>
                        {isEditing ? (
                          // Edit Mode
                          <div className="space-y-3">
                            <div className="flex gap-2">
                              <span className="text-lg font-semibold text-gray-400 mt-2">{index + 1}.</span>
                              <Input
                                value={editingAgendaItem.title}
                                onChange={(e) => setEditingAgendaItem({...editingAgendaItem, title: e.target.value})}
                                placeholder="Item title"
                                className="flex-1 font-semibold"
                              />
                            </div>
                            <Textarea
                              value={editingAgendaItem.description}
                              onChange={(e) => setEditingAgendaItem({...editingAgendaItem, description: e.target.value})}
                              placeholder="Description"
                              rows={3}
                            />
                            <div className="flex gap-3 items-center">
                              <Select value={editingAgendaItem.priority} onValueChange={(v) => setEditingAgendaItem({...editingAgendaItem, priority: v})}>
                                <SelectTrigger className="w-28">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="low">Low</SelectItem>
                                  <SelectItem value="normal">Normal</SelectItem>
                                  <SelectItem value="high">High</SelectItem>
                                </SelectContent>
                              </Select>
                              <div className="flex items-center gap-1">
                                <Input
                                  type="number"
                                  value={editingAgendaItem.estimated_duration}
                                  onChange={(e) => setEditingAgendaItem({...editingAgendaItem, estimated_duration: parseInt(e.target.value) || 10})}
                                  className="w-16"
                                  min={5}
                                  max={60}
                                />
                                <span className="text-sm text-gray-500">mins</span>
                              </div>
                              <div className="flex-1" />
                              <Button size="sm" variant="ghost" onClick={handleCancelEdit}>
                                <X className="w-4 h-4 mr-1" />
                                Cancel
                              </Button>
                              <Button size="sm" onClick={handleSaveAgendaItem} className="bg-green-600 hover:bg-green-700">
                                <Save className="w-4 h-4 mr-1" />
                                Save
                              </Button>
                            </div>
                          </div>
                        ) : (
                          // View Mode
                          <div className="flex items-start gap-3">
                            {/* Reorder buttons */}
                            {canEdit && (
                              <div className="flex flex-col gap-0.5">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-6 w-6 p-0"
                                  onClick={() => handleMoveAgendaItem(item.id, 'up')}
                                  disabled={index === 0}
                                  title="Move up"
                                >
                                  <ChevronUp className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-6 w-6 p-0"
                                  onClick={() => handleMoveAgendaItem(item.id, 'down')}
                                  disabled={index === agendaItems.length - 1}
                                  title="Move down"
                                >
                                  <ChevronDown className="w-4 h-4" />
                                </Button>
                              </div>
                            )}
                            
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-lg font-semibold">{index + 1}. {item.title}</span>
                                <span className={`px-2 py-0.5 text-xs rounded ${
                                  item.priority === 'high' ? 'bg-red-100 text-red-700' :
                                  item.priority === 'low' ? 'bg-gray-100 text-gray-700' :
                                  'bg-blue-100 text-blue-700'
                                }`}>
                                  {item.priority}
                                </span>
                              </div>
                              <p className="text-gray-600 mb-2">{item.description}</p>
                              <p className="text-xs text-gray-500">
                                Submitted by {item.submitted_by_name} • {item.estimated_duration} mins
                              </p>
                            </div>
                            
                            {/* Action buttons */}
                            {canEdit && (
                              <div className="flex gap-1">
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={() => handleEditAgendaItem(item)}
                                  title="Edit"
                                  data-testid={`edit-agenda-${index + 1}`}
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  className="text-red-500"
                                  onClick={() => handleDeleteAgendaItem(item.id)}
                                  title="Delete"
                                  data-testid={`delete-agenda-${index + 1}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Attendees Tab */}
        <TabsContent value="attendees" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Meeting Attendees</CardTitle>
              <CardDescription>Manage who attended and apologies</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <h4 className="font-medium mb-3">Chair</h4>
                  <Select 
                    value={meeting.chair_id || ''} 
                    onValueChange={(v) => {
                      axios.put(`${API}/scheduled-meetings/${id}`, { chair_id: v })
                        .then(() => { toast.success('Chair updated'); fetchData(); })
                        .catch(() => toast.error('Failed to update'));
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select chair..." />
                    </SelectTrigger>
                    <SelectContent>
                      {users.map((u) => (
                        <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <h4 className="font-medium mb-3">Secretary</h4>
                  <Select 
                    value={meeting.secretary_id || ''} 
                    onValueChange={(v) => {
                      axios.put(`${API}/scheduled-meetings/${id}`, { secretary_id: v })
                        .then(() => { toast.success('Secretary updated'); fetchData(); })
                        .catch(() => toast.error('Failed to update'));
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select secretary..." />
                    </SelectTrigger>
                    <SelectContent>
                      {users.map((u) => (
                        <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="mt-6">
                <h4 className="font-medium mb-3">Attendees</h4>
                <p className="text-sm text-gray-500">
                  Attendee tracking will be available after the meeting
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Minutes Tab */}
        <TabsContent value="minutes" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Meeting Minutes</CardTitle>
              <CardDescription>Create and manage meeting minutes</CardDescription>
            </CardHeader>
            <CardContent>
              {!minutes ? (
                <div className="text-center py-8">
                  <FileText className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                  <p className="text-gray-500 mb-4">No minutes created yet</p>
                  {isAdmin && (
                    <Button onClick={createMinutes} className="bg-gradient-to-r from-blue-500 to-green-500">
                      <Plus className="w-4 h-4 mr-2" />
                      Create Minutes
                    </Button>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                        minutes.status === 'approved' ? 'bg-green-100 text-green-700' :
                        minutes.status === 'pending_approval' ? 'bg-orange-100 text-orange-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {minutes.status === 'approved' ? 'Approved' :
                         minutes.status === 'pending_approval' ? 'Pending Approval' : 'Draft'}
                      </span>
                    </div>
                    <div className="flex gap-2">
                      <Link to={`/meeting-minutes/${minutes.id}`}>
                        <Button variant="outline">
                          <Edit className="w-4 h-4 mr-2" />
                          Edit Minutes
                        </Button>
                      </Link>
                      <Button variant="outline" onClick={async () => {
                        try {
                          const response = await axios.get(`${API}/meeting-minutes/${minutes.id}/pdf`, { responseType: 'blob' });
                          const url = window.URL.createObjectURL(new Blob([response.data]));
                          const link = document.createElement('a');
                          link.href = url;
                          link.setAttribute('download', `HLRA_Minutes_${meeting.meeting_date}.pdf`);
                          document.body.appendChild(link);
                          link.click();
                          link.remove();
                          toast.success('Minutes downloaded');
                        } catch { toast.error('Failed to download'); }
                      }}>
                        <Download className="w-4 h-4 mr-2" />
                        Download PDF
                      </Button>
                    </div>
                  </div>
                  <p className="text-sm text-gray-500">
                    Prepared by: {minutes.prepared_by_name}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
